import { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../app/store';
import apiClient from '../../core/config/api';
import "./manage_users.css";
import AddUserModal from "./ManageUsers/AddUserModal";
import UserDetailsPanel from "./ManageUsers/UserDetailsPanel";
import AssignCreditsModal from "./ManageUsers/AssignCreditsModal";
import RemoveCreditsModal from "./ManageUsers/RemoveCreditsModal";
import DeleteUserModal from "./ManageUsers/DeleteUserModal";

const ManageUsers = () => {
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const currentUserRoleId = currentUser?.role_id;
    const isMember = currentUserRoleId === 6;
    const [showPopup, setShowPopup] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const [resetStep, setResetStep] = useState(1);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteStep, setDeleteStep] = useState(1);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const [showRemoveCredits, setShowRemoveCredits] = useState(false);

    const [showAssignCredits, setShowAssignCredits] = useState(false);

    const [showUserDetails, setShowUserDetails] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [subUsers, setSubUsers] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(5);
    const [goToPageInput, setGoToPageInput] = useState("");
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [userStatus, setUserStatus] = useState(true); // true = VERIFIED
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [showActivatePopup, setShowActivatePopup] = useState(false);
    const [isStatusUpdating, setIsStatusUpdating] = useState(false);

    const [rootUserCredits, setRootUserCredits] = useState({
        total_credits: 0,
        available_credits: 0,
        used_credits: 0,
    });
    const [subUserStats, setSubUserStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
    });
    const [returnedCredits, setReturnedCredits] = useState<number>(0);

    const deleteAutoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectedUserId = useMemo(() => {
        const maybeId = selectedUser?.id ?? selectedUser?.user_id;
        if (maybeId === undefined || maybeId === null || maybeId === "") return null;
        const numericId = Number(maybeId);
        return Number.isNaN(numericId) ? null : numericId;
    }, [selectedUser]);

    const canManageSelectedUser = Boolean(selectedUserId);

    const remainingAccounts = useMemo(() => {
        const remaining = 3 - (subUsers?.length || 0);
        return remaining < 0 ? 0 : remaining;
    }, [subUsers]);

    const addUserTooltip = useMemo(() => {
        const label = remainingAccounts === 1 ? "account" : "accounts";
        return `${remainingAccounts} ${label} remaining`;
    }, [remainingAccounts]);

    const formatName = (value?: string) => {
        if (!value) return "-";
        return value
            .split(/\s+/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(" ");
    };

    const selectedUserName = useMemo(() => {
        if (!selectedUser) return "-";
        return formatName([selectedUser?.first_name, selectedUser?.last_name].filter(Boolean).join(" ")) || "-";
    }, [selectedUser]);

    const clearDeleteAutoCloseTimer = () => {
        if (deleteAutoCloseTimer.current) {
            clearTimeout(deleteAutoCloseTimer.current);
            deleteAutoCloseTimer.current = null;
        }
    };

    const handleCloseDeleteModal = () => {
        clearDeleteAutoCloseTimer();
        setShowDelete(false);
        setDeleteStep(1);
    };

    const isResetFormValid = useMemo(() => {
        return password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;
    }, [confirmPassword, password]);

    const roleIdLabelMap: Record<number, string> = {
        1: "Admin",
        2: "User",
        4: "IT Admin",
        5: "Datacaptive",
        6: "Member",
    };

    const getRoleLabel = (user: any) => {
        if (!user) return "-";
        const roleId = Number(user.role_id);
        if (roleId && roleIdLabelMap[roleId]) return roleIdLabelMap[roleId];

        if (user.role) return user.role;
        if (user.role_id) return String(user.role_id);
        return "-";
    };

    const formatTimestamp = (value: any) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    const formatNumber = (value?: number | null) => {
        if (value === undefined || value === null || Number.isNaN(value)) return "0";
        return value.toLocaleString("en-US");
    };

    const formatStatus = (value?: string) => {
        if (!value) return "-";
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    };

    const isUserActive = (user: any) => {
        if (!user) return false;
        const isVerified = user?.is_verified;
        if (isVerified === true) return true;
        if (isVerified === false) return false;

        const status = (user?.account_status ?? user?.status ?? "").toString().trim().toLowerCase();
        const isActiveFlag = user?.is_active;
        if (status === "active") return true;
        if (status === "inactive" || status === "pending") return false;
        if (isActiveFlag === true) return true;
        if (isActiveFlag === false) return false;
        return false;
    };

    useEffect(() => {
        if (selectedUser) {
            setUserStatus(isUserActive(selectedUser));
        }
    }, [selectedUser]);

    useEffect(() => {
        return () => {
            clearDeleteAutoCloseTimer();
        };
    }, []);

    const fetchRootUserCredits = async () => {
        try {
            const response = await apiClient.get('/account/re/credits/', {
                params: { _t: Date.now() },
            });
            setRootUserCredits({
                total_credits: response.data.max_upload_limit || 0,
                available_credits: response.data.remaining_credits || 0,
                used_credits: response.data.used_credits || 0,
            });
        } catch {
            // no-op
        }
    };

    const fetchSubUserStats = async () => {
        try {
            const response = await apiClient.get('/re/sub-users', {
                params: { page: 1, page_size: 1000, _t: Date.now() },
            });
            const users = response.data.data || [];
            const total = response.data.total || 0;
            const active = users.filter((u: any) => isUserActive(u)).length;
            const inactive = users.filter((u: any) => !isUserActive(u)).length;
            setSubUserStats({ total, active, inactive });
        } catch {
            // no-op
        }
    };

    const fetchSubUsers = async (page: number = currentPage) => {
        try {
            setLoading(true);
            const params = {
                page,
                page_size: pageSize,
                search: searchTerm || undefined,
                _t: Date.now(),
            };
            const response = await apiClient.get('/re/sub-users', { params });
            const users = response.data.data || [];

            const usersWithCredits = await Promise.all(
                users.map(async (user: any) => {
                    try {
                        const creditRes = await apiClient.get(`/re/sub-users/${user.id}/credits`);
                        return {
                            ...user,
                            credits_assigned: creditRes.data.allocated_credits,
                            credits_remaining: creditRes.data.remaining_credits,
                            used_credits: creditRes.data.used_credits,
                            current_balance: creditRes.data.remaining_credits,
                        };
                    } catch {
                        return user;
                    }
                })
            );
            let filtered = usersWithCredits;
            const term = searchTerm.trim().toLowerCase();
            if (term) {
                filtered = usersWithCredits.filter((user: any) => {
                    const name = formatName(
                        [user?.first_name, user?.last_name]
                            .filter(Boolean)
                            .join(" ") ||
                        user?.full_name ||
                        "-"
                    ).toLowerCase();
                    const emailValue = (user?.email || "-").toLowerCase();
                    const roleValue = getRoleLabel(user).toString().toLowerCase();
                    const creditsFields = [
                        user?.current_balance,
                        user?.credits_remaining,
                        user?.credits_assigned,
                        user?.allocated_credits,
                        user?.used_credits,
                    ]
                        .filter((v) => v !== undefined && v !== null)
                        .map((v) => String(v).toLowerCase());

                    return (
                        name.includes(term) ||
                        emailValue.includes(term) ||
                        roleValue.includes(term) ||
                        creditsFields.some((v) => v.includes(term))
                    );
                });
            }
            setSubUsers(filtered);
            setTotalPages(Math.max(1, Math.ceil((response.data.total || 0) / pageSize)));
            setCurrentPage(page);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchSubUsers(1);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            fetchSubUsers(page);
        }
    };

    const handleGoToPage = () => {
        const page = parseInt(goToPageInput);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setGoToPageInput(String(page));
        }
    };

    const renderPageNumbers = () => {
        const pages = [] as (number | string)[];
        const maxVisiblePages = 5;
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, "...", totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, "...", currentPage, "...", totalPages);
            }
        }

        return pages;
    };

    const handleResetPassword = async () => {
        if (!canManageSelectedUser || isResetting) return;
        try {
            setIsResetting(true);
            setError("");
            const response = await apiClient.put(`/re/sub-users/${selectedUserId}/password`, {
                password,
                confirm_password: confirmPassword,
            });
            setSelectedUser(response?.data || selectedUser);
            setResetStep(2);
            await fetchSubUsers(currentPage);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.response?.data?.message || "Failed to reset password");
        } finally {
            setIsResetting(false);
        }
    };

    const handleUpdateStatus = async (nextStatus: 'active' | 'inactive') => {
        if (!canManageSelectedUser) return;
        try {
            setIsStatusUpdating(true);
            setError("");

            // Optimistic update
            setUserStatus(nextStatus === 'active');
            setSelectedUser((prev: any) => (prev ? { ...prev, is_verified: nextStatus === 'active' } : prev));

            const response = await apiClient.put(`/re/sub-users/${selectedUserId}/status`, {
                is_verified: nextStatus === 'active',
            });
            const updated = {
                ...selectedUser,
                is_verified: nextStatus === 'active',
                ...(response?.data || {}),
            };
            setSelectedUser(updated);
            setUserStatus(isUserActive(updated));

            // Also update the user in subUsers list with response data so table shows correct status immediately
            if (response?.data) {
                setSubUsers((prevUsers: any[]) =>
                    prevUsers.map((u: any) => {
                        if (Number(u?.id ?? u?.user_id) === Number(selectedUserId)) {
                            const merged = {
                                ...u,
                                is_verified: nextStatus === 'active',
                                ...response.data,
                            };

                            return merged;

                        }
                        return u;
                    })
                );
            }

            // Refresh list + stats + credits to ensure UI matches the persisted backend state
            await fetchSubUsers(currentPage);
            await fetchSubUserStats();
            await fetchRootUserCredits();
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.response?.data?.message || "Failed to update status");
        } finally {
            setIsStatusUpdating(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!canManageSelectedUser) return;
        try {
            setIsDeleting(true);
            setError("");

            const userCredits =
                selectedUser?.credits_assigned ??
                selectedUser?.allocated_credits ??
                selectedUser?.current_balance ??
                0;
            setReturnedCredits(Number(userCredits) || 0);
            await apiClient.delete(`/re/sub-users/${selectedUserId}`);
            setDeleteStep(2);
            clearDeleteAutoCloseTimer();
            deleteAutoCloseTimer.current = setTimeout(() => {
                handleCloseDeleteModal();
            }, 2500);
            await fetchSubUsers(currentPage);
            await fetchSubUserStats();
            await fetchRootUserCredits();
            setSelectedUser(null);
            setShowUserDetails(false);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.response?.data?.message || "Failed to delete user");
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        fetchSubUsers(1);
        fetchRootUserCredits();
        fetchSubUserStats();
    }, []);

    useEffect(() => {
        const fetchSelectedUserCredits = async () => {
            if (!selectedUserId || !showUserDetails) return;
            try {
                const res = await apiClient.get(`/re/sub-users/${selectedUserId}/credits`);
                setSelectedUser((prev: any) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        credits_assigned: res.data.allocated_credits,
                        current_balance: res.data.remaining_credits,
                        used_credits: res.data.used_credits,
                    };
                });
            } catch (err: any) {
                setError(err?.response?.data?.detail || "Failed to load user credits");
            }
        };
        fetchSelectedUserCredits();
    }, [selectedUserId, showUserDetails]);

    if (isMember) {
        return (
            <div className="dashboard-page">
                <h1>User Management</h1>
                <p>You do not have access to manage users.</p>
            </div>
        );
    }

    return (
        <>
            <div className="dashboard-page">
                <h1>Team Management</h1>
                <p>Invite team members, assign verification credits, and manage account access from one place.</p>
            </div>

            <div className="row">
                <div className="col-md-10">
                    <div className="team-heading-row">
                        <div className="d-flex flex-row align-items-center">
                            <div>
                                <img src="/images/m-users.png" alt="" />
                            </div>
                            <div>
                                <p className="user-list">Team Members</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="credit-logs-btn"
                            onClick={() => navigate("/credit-logs")}
                        >
                            Credit Logs
                        </button>
                    </div>

                    <div className="user-management-slider">

                        {/* LEFT PANEL – USER LIST (existing UI) */}
                        <div className={`slider-panel ${showUserDetails ? "slide-left" : ""}`}>

                            {/* 🔴EXISTING */}
                            <div className="user-management-box">

                                <div>
                                    <div className="d-flex flex-row justify-content-between">
                                        <div className="search-wrapper">
                                            <img
                                                src="/images/search-icon.png"
                                                alt="search"
                                                className="search-icon left"
                                            />
                                            <input
                                                type="text"
                                                className="search-input"
                                                placeholder="Search by name or email address..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                            />
                                        </div>

                                    </div>

                                    {!showUserDetails && (
                                        <div className="table-wrapper table-responsive">
                                            <table className="custom-table team-table">
                                                <thead>
                                                    <tr>
                                                        <th>User Name</th>
                                                        <th>Email Address</th>
                                                        <th>Credits Assigned</th>
                                                        <th>Account Status</th>
                                                        <th>Last Login</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan={5} style={{ textAlign: "center" }}>
                                                                Loading users...
                                                            </td>
                                                        </tr>
                                                    ) : subUsers.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={5} style={{ textAlign: "center" }}>
                                                                No users found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        subUsers.map((user: any) => {

                                                            const rawName =
                                                                [user?.first_name, user?.last_name]
                                                                    .filter(Boolean)
                                                                    .join(" ") ||
                                                                user?.full_name ||
                                                                "-";
                                                            const name = formatName(rawName);

                                                            const emailValue = user?.email || "-";
                                                            const creditsValue =
                                                                user?.current_balance ??
                                                                user?.credits_remaining ??
                                                                user?.credits_assigned ??
                                                                user?.allocated_credits ??
                                                                "-";
                                                            const statusValue = formatStatus(user?.is_verified ? "active" : "inactive");
                                                            const lastActiveValue = formatTimestamp(
                                                                user?.last_active ??
                                                                    user?.last_login ??
                                                                    user?.updated_at ??
                                                                    user?.created_at,
                                                            );

                                                            return (
                                                                <tr key={user?.id ?? user?.user_id ?? emailValue}>
                                                                    <td>
                                                                        <div className="user-name-cell">
                                                                            <img
                                                                                src="/images/small-gear.png"
                                                                                alt="settings"
                                                                                style={{ cursor: "pointer" }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setSelectedUser(user);
                                                                                    setShowUserDetails(true);
                                                                                    setUserStatus(isUserActive(user));
                                                                                }}
                                                                            />
                                                                            <span className="user-name-cell-divider" />
                                                                            <span>{name}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{emailValue}</td>
                                                                    <td>{creditsValue}</td>
                                                                    <td>{statusValue}</td>
                                                                    <td>{lastActiveValue}</td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>

                                            {totalPages > 1 && (
                                                <div className="pagination-container">
                                                    <button
                                                        className="pagination-arrow"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        &lt;
                                                    </button>

                                                    {renderPageNumbers().map((page, index) =>
                                                        page === "..." ? (
                                                            <span
                                                                key={`ellipsis-${index}`}
                                                                className="pagination-ellipsis"
                                                            >
                                                                ...
                                                            </span>
                                                        ) : (
                                                            <button
                                                                key={page}
                                                                className={`pagination-number ${
                                                                    currentPage === page ? "active" : ""
                                                                }`}
                                                                onClick={() => handlePageChange(page as number)}
                                                            >
                                                                {page}
                                                            </button>
                                                        ),
                                                    )}

                                                    <button
                                                        className="pagination-arrow"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        &gt;
                                                    </button>

                                                    <div className="go-to-page">
                                                        <span>Go to page</span>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={totalPages}
                                                            value={goToPageInput}
                                                            onChange={(e) => setGoToPageInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") { handleGoToPage(); }
                                                            }}
                                                            onBlur={handleGoToPage}
                                                            className="page-input"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!showUserDetails && (
                                        <div className="invite-user-row">
                                            <button
                                                type="button"
                                                className="add-users"
                                                onClick={() => {
                                                    if (subUsers.length < 3) setShowPopup(true);
                                                }}
                                                disabled={subUsers.length >= 3}
                                                title={addUserTooltip}
                                            >
                                                <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
                                                    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                                </svg>
                                                <span>Invite User</span>
                                            </button>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>

                        {/* RIGHT PANEL – USER DETAILS */}
                        <UserDetailsPanel
                            isOpen={showUserDetails}
                            selectedUserName={selectedUserName}

                            selectedUserRole={getRoleLabel(selectedUser)}
                            selectedUserLastActive={formatTimestamp(
                                selectedUser?.last_active ??
                                    selectedUser?.last_login ??
                                    selectedUser?.updated_at ??
                                    selectedUser?.created_at,
                            )}
                            selectedUserCurrentBalance={selectedUser?.current_balance}
                            selectedUserUsedCredits={selectedUser?.used_credits}
                            selectedUserTotalAssigned={selectedUser?.credits_assigned}
                            userStatus={userStatus}
                            onBack={() => {
                                setShowUserDetails(false);
                                setSelectedUser(null);
                            }}
                            onToggleStatus={() => {
                                if (userStatus) {
                                    setShowDeactivateModal(true);
                                } else {
                                    setShowActivatePopup(true);
                                }
                            }}
                            onResetPassword={() => setShowReset(true)}
                            onDeleteUser={() => {
                                if (canManageSelectedUser) {
                                    setShowDelete(true);
                                    setDeleteStep(1);
                                }
                            }}
                            canDeleteUser={canManageSelectedUser}
                            onRemoveCredits={() => {
                                setShowRemoveCredits(true);
                            }}
                            onAssignCredits={() => {
                                setShowAssignCredits(true);
                            }}
                        />

                    </div>

                    {/* add user popup */}

                    <AddUserModal
                        isOpen={showPopup}
                        onClose={() => setShowPopup(false)}
                        onUserCreated={() => {
                            fetchSubUsers(currentPage);
                            fetchSubUserStats();
                            fetchRootUserCredits();
                        }}
                    />

                    {/* reset password popup  */}

                    {showReset && (
                        <div className="popup-overlay" onClick={() => setShowReset(false)}>
                            <div
                                className="popup-box"
                                style={{
                                    backgroundImage: "url('/images/popup-back.png')",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center",
                                    backgroundSize: "inherit",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* HEADER */}
                                <div
                                    className="d-flex flex-row justify-content-between"
                                    style={{ marginBottom: resetStep === 1 ? 25 : 0 }}
                                >
                                    <p className="user-info">
                                        {resetStep === 1 ? "Reset Password" : ""}
                                    </p>
                                    <img
                                        src="/images/cancel.png"
                                        alt="close"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                            setShowReset(false);
                                            setResetStep(1);
                                        }}
                                    />
                                </div>

                                {/* STEP 1 */}
                                {resetStep === 1 && (
                                    <>
                                        <div className="user-name">
                                            <p className="user-of-the-year">
                                                User:<span style={{ marginLeft: 4 }}>{selectedUserName}</span>
                                            </p>
                                        </div>

                                        {error && <div className="email-alert-danger mt-2">{error}</div>}
                                        
                                        <form className="password-form">
                                            {/* Password */}
                                            <div className="password-group">
                                                <label className="password-label">
                                                    Enter Temporary Password
                                                </label>

                                                <div className="password-input-wrapper">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="password-input"
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                    />

                                                    <img
                                                        src={
                                                            showPassword
                                                                ? "/images/grey-eye.png"
                                                                : "/images/md-eye-off.png"
                                                        }
                                                        alt="toggle password"
                                                        className="password-toggle-icon"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    />
                                                </div>
                                            </div>

                                            {/* Confirm Password */}
                                            <div className="password-group">
                                                <label className="password-label">Confirm Password</label>

                                                <div className="password-input-wrapper">
                                                    <input
                                                        type={showConfirm ? "text" : "password"}
                                                        className="password-input"
                                                        value={confirmPassword}
                                                        onChange={(e) =>
                                                            setConfirmPassword(e.target.value)
                                                        }
                                                    />

                                                    <img
                                                        src={
                                                            showConfirm
                                                                ? "/images/grey-eye.png"
                                                                : "/images/md-eye-off.png"
                                                        }
                                                        alt="toggle password"
                                                        className="password-toggle-icon"
                                                        onClick={() => setShowConfirm(!showConfirm)}
                                                    />
                                                </div>
                                            </div>
                                        </form>

                                        <div className="d-flex flex-row justify-content-between">
                                            <button
                                                type="button"
                                                className="cancel"
                                                disabled={!isResetFormValid || isResetting}
                                                onClick={() => {
                                                    setShowReset(false);
                                                    setResetStep(1);
                                                }}
                                            >

                                                Cancel
                                            </button>

                                            <button
                                                type="button"
                                                className="save"
                                                disabled={!isResetFormValid || isResetting}
                                                onClick={handleResetPassword}
                                            >

                                                {isResetting ? "Resetting..." : "Reset"}
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* STEP 2 */}
                                {resetStep === 2 && (
                                    <>
                                        <div className="text-center">
                                            <img
                                                src="/images/shadow-check.png"
                                                className="invitation-img"
                                                alt=""
                                            />

                                            <p className="email-invitation">
                                                Password Reset Successfully
                                            </p>

                                            <p className="invitation">
                                                Your password has been updated successfully. You can now sign in using your new credentials.
                                            </p>

                                            <div className="d-flex flex-row justify-content-center" style={{ marginTop: 30 }}>
                                                <button
                                                    type="button"
                                                    className="f-confirm"
                                                    onClick={() => {
                                                        setShowReset(false);
                                                        setResetStep(1);
                                                        setPassword("");
                                                        setConfirmPassword("");
                                                    }}
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}


                    <DeleteUserModal
                        isOpen={showDelete}
                        deleteStep={deleteStep}
                        selectedUserName={selectedUserName}
                        selectedUserEmail={selectedUser?.email}
                        returnedCredits={returnedCredits}
                        error={error}
                        isDeleting={isDeleting}
                        onClose={handleCloseDeleteModal}
                        onConfirmDelete={handleDeleteUser}
                        onResetStep={() => setDeleteStep(1)}
                        formatNumber={formatNumber}
                    />


                    {/* Remove credits*/}

                    <RemoveCreditsModal
                        isOpen={showRemoveCredits}
                        onClose={() => setShowRemoveCredits(false)}
                        selectedUserId={selectedUserId}
                        selectedUserName={selectedUserName}
                        onCreditsRemoved={() => {
                            fetchSubUsers(currentPage);
                            fetchSubUserStats();
                            fetchRootUserCredits();
                        }}
                    />

                    {/* Assign credits */}

                    <AssignCreditsModal
                        isOpen={showAssignCredits}
                        onClose={() => setShowAssignCredits(false)}
                        selectedUserId={selectedUserId}
                        selectedUserName={selectedUserName}
                        onCreditsAssigned={() => {
                            fetchSubUsers(currentPage);
                            fetchSubUserStats();
                            fetchRootUserCredits();
                        }}
                    />


                    {/* deactive and deactive popup  */}
                    {showDeactivateModal && (
                        <div className="popup-overlay" onClick={() => setShowDeactivateModal(false)}>
                            <div
                                className="active-popup-box"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* HEADER */}
                                <div className="d-flex flex-row justify-content-end">
                                    <div>
                                        <img
                                            src="/images/cancel.png"
                                            alt="close"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setShowDeactivateModal(false)}
                                            width="30"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <p className="deactivate-info">Deactivate User</p>
                                </div>

                                <div className="mt-3">
                                    <p className="permanently-f">
                                        {selectedUserName} (<em>{selectedUser?.email || '-'}</em>) will no longer have access. You can reactivate anytime.
                                    </p>
                                </div>

                                <div className="d-flex flex-row justify-content-between mt-1">
                                    <button
                                        type="button"
                                        className="deactivate-cancel-bttn"
                                        onClick={() => setShowDeactivateModal(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="saved-delete"
                                        disabled={isStatusUpdating}
                                        onClick={async () => {
                                            await handleUpdateStatus('inactive');
                                            setShowDeactivateModal(false);
                                        }}
                                    >
                                        {isStatusUpdating ? 'Updating...' : 'Confirm Deactivate'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showActivatePopup && (
                        <div className="popup-overlay" onClick={() => setShowActivatePopup(false)}>
                            <div className="active-popup-box" onClick={(e) => e.stopPropagation()}>
                                {/* HEADER */}
                                <div className="d-flex flex-row justify-content-end">
                                    <div>
                                        <img
                                            src="/images/cancel.png"
                                            alt="close"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setShowActivatePopup(false)}
                                            width="30"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <p className="deactivate-info">Activate User?</p>
                                </div>

                                <div className="mt-3">
                                    <p className="permanently-f">
                                        {selectedUserName} (<em>{selectedUser?.email || '-'}</em>) will now have access.
                                    </p>
                                </div>

                                <div className="d-flex flex-row justify-content-between mt-2">
                                    <button
                                        className="deactivate-cancel-bttn"
                                        onClick={() => setShowActivatePopup(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="save"
                                        disabled={isStatusUpdating}
                                        onClick={async () => {
                                            await handleUpdateStatus('active');
                                            setShowActivatePopup(false);
                                        }}
                                    >
                                        {isStatusUpdating ? 'Updating...' : 'Confirm Activation'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
                <div className="col-md-2">

                    <div className="user-management-box-1">
                        <p className="total-credits">
                            Total Credits
                        </p>
                        <span className="total-credits">{formatNumber(rootUserCredits.total_credits)}</span>
                        <div className="devider"></div>
                        <p className="total-credits">
                            Available Credits
                        </p>
                        <span className="total-credits-r">{formatNumber(rootUserCredits.available_credits)}</span>
                    </div>

                    {!isMember && (
                        <div className="user-management-box-1">
                            <p className="total-credits">
                                Total Users
                            </p>
                            <span className="total-credits">{formatNumber(subUserStats.total)}</span>
                            <div className="devider"></div>
                            <p className="total-credits">
                                Active Users
                            </p>
                            <span className="total-credits-g">{formatNumber(subUserStats.active)}</span>
                            <div className="devider"></div>
                            <p className="total-credits">
                                Inactive Users
                            </p>
                            <span className="total-credits-t">{formatNumber(subUserStats.inactive)}</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ManageUsers